import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/auth/authStore';
import { useMe } from '@/auth/useAuth';
import runtimeDocument from './runtimeDocument.html?raw';
import { physicsLabNeedsSessionRestore, physicsLabReturn } from './physicsLabNavigation';
import { PHYSICS_LAB_RUNTIME } from './runtimeManifest';

/** Either account kind can use the Lab without mounting an account layout. */
export function PhysicsLabPage() {
  const location = useLocation();
  const bootstrapped = useAuthStore((state) => state.bootstrapped);
  const tokens = useAuthStore((state) => state.tokens);
  const kid = useMe('kid');
  const user = useMe('user');
  const ready =
    (tokens.kid && kid.data?.kind === 'kid' && !kid.isError && !kid.isLoading) ||
    (tokens.user && user.data?.kind === 'user' && !user.isError && !user.isLoading);
  if (
    !bootstrapped ||
    (!ready && ((tokens.kid && kid.isLoading) || (tokens.user && user.isLoading)))
  ) {
    return (
      <main
        className="flex h-[100dvh] items-center justify-center bg-canvas text-ink-soft"
        role="status"
      >
        Loading session…
      </main>
    );
  }
  if (!ready) {
    const parentEntry = new URLSearchParams(location.search).get('from') === 'portal';
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return (
      <Navigate
        replace
        state={{ from: location }}
        to={parentEntry ? '/portal/login' : `/learn/login?returnTo=${encodeURIComponent(returnTo)}`}
      />
    );
  }
  return <AuthenticatedPhysicsLab />;
}

function AuthenticatedPhysicsLab() {
  const frame = useRef<HTMLIFrameElement>(null);
  const [params] = useSearchParams();
  const tokens = useAuthStore((state) => state.tokens);
  const from = params.get('from');
  const returnTo = physicsLabReturn(
    from && from !== 'guest' ? from : tokens.kid ? 'learn' : 'portal',
    params.get('year'),
  );
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const returnToAirbotix = useCallback(() => {
    // Restore the destination surface when only the other account kind is signed in.
    if (physicsLabNeedsSessionRestore(returnTo, useAuthStore.getState().tokens)) {
      window.location.assign(returnTo);
    } else {
      navigate(returnTo);
    }
  }, [navigate, returnTo]);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = window.setTimeout(() => setStatus('error'), 20000);
    return () => window.clearTimeout(timer);
  }, [attempt, status]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow)
        return;
      if (event.data?.channel !== 'airbotix-physics-lab-v1') return;
      if (event.data.type === 'ready' || event.data.type === 'error') {
        setStatus(event.data.type);
      }
      if (event.data.type === 'exit') returnToAirbotix();
    };
    window.addEventListener('message', receive);
    return () => {
      window.removeEventListener('message', receive);
    };
  }, [returnToAirbotix]);

  return (
    <main
      className="fixed inset-0 z-50 h-[100dvh] overflow-hidden bg-canvas text-ink"
      aria-label="Airbotix Physics Lab"
    >
      <iframe
        key={attempt}
        ref={frame}
        srcDoc={runtimeDocument.replace(
          '<head>',
          `<head><base href="${new URL(PHYSICS_LAB_RUNTIME.entry.replace(/index\.html$/, ''), window.location.origin).href}">`,
        )}
        title="Airbotix Physics Lab — circuits and mechanics"
        className={`h-full w-full border-0 ${status === 'ready' ? '' : 'invisible'}`}
        tabIndex={status === 'ready' ? 0 : -1}
        allow="fullscreen"
        allowFullScreen
        onError={() => setStatus('error')}
      />
      {status !== 'ready' && (
        <div className="absolute inset-0 flex items-center justify-center bg-canvas p-5">
          <section
            className="w-full max-w-md rounded-3xl border border-hairline bg-canvas-pure p-6 shadow-card-soft"
            role={status === 'error' ? 'alert' : 'status'}
          >
            <h1 className="text-xl font-extrabold">
              {status === 'error' ? 'The Lab could not open' : 'Preparing your Physics Lab…'}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-ink-soft">
              {status === 'error'
                ? 'Check your connection, then try again. Your saved circuit has not been cleared.'
                : 'Opening the circuit workspace and simulation engine.'}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {status === 'error' && (
                <button
                  className="btn-pill-primary"
                  onClick={() => {
                    setStatus('loading');
                    setAttempt((value) => value + 1);
                  }}
                >
                  Try again
                </button>
              )}
              <Link
                to={returnTo}
                className="btn-pill-secondary"
                onClick={(event) => {
                  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
                  event.preventDefault();
                  returnToAirbotix();
                }}
              >
                Back to Airbotix
              </Link>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
